import React, { useContext, useMemo, useState } from 'react';
import { Flex, Table, Tag, Tooltip, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table/interface';

import NozomiReconciliationContext from '@totem/components/administration/reconciliation/nozomi/NozomiReconciliationContext';
import {
  ReconciliationCounts,
  ReconciliationRow,
} from '@totem/components/administration/reconciliation/nozomi/types';
import {
  buildRows,
  getTotals,
} from '@totem/components/administration/reconciliation/nozomi/utilities';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';

const formatCount = (value: number) =>
  value === 0 ? (
    <Typography.Text type="secondary">–</Typography.Text>
  ) : (
    value.toLocaleString()
  );

const AssetsPerDevice = ({ counts }: { counts: ReconciliationCounts }) => {
  if (counts.portalSynced === 0) {
    return <Typography.Text type="secondary">–</Typography.Text>;
  }

  const ratio = counts.assetKeys / counts.portalSynced;
  return (
    <Tooltip
      title={`${counts.assetKeys.toLocaleString()} Vantage assets on ${counts.portalSynced.toLocaleString()} portal devices. ${counts.multiAssetDevices.toLocaleString()} devices hold more than one asset; the most on one device is ${counts.maxAssetsPerDevice}.`}
    >
      <Flex vertical align="flex-end">
        <Typography.Text strong={ratio >= 1.1}>
          {`${ratio.toFixed(2)} : 1`}
        </Typography.Text>
        <Typography.Text
          type="secondary"
          style={{ fontSize: 12, whiteSpace: 'nowrap' }}
        >
          {counts.merged > 0
            ? `+${counts.merged.toLocaleString()} merged · max ${counts.maxAssetsPerDevice}`
            : 'no merges'}
        </Typography.Text>
      </Flex>
    </Tooltip>
  );
};

const Variance = ({ counts }: { counts: ReconciliationCounts }) => {
  if (!counts.processed) {
    return <Typography.Text type="secondary">–</Typography.Text>;
  }

  return counts.variance === 0 ? (
    <Tag color="success">0</Tag>
  ) : (
    <Tooltip title="Assets that are neither skipped nor on a portal device. Check this sensor's Log Sync entry.">
      <Tag color="error">
        {counts.variance > 0 ? '+' : ''}
        {counts.variance.toLocaleString()}
      </Tag>
    </Tooltip>
  );
};

const countColumn = (
  title: string,
  key: keyof ReconciliationCounts,
  tooltip: string,
) => ({
  title: <Tooltip title={tooltip}>{title}</Tooltip>,
  key,
  align: 'right' as const,
  render: (_: unknown, record: ReconciliationRow) =>
    formatCount(record[key] as number),
});

const columns: ColumnsType<ReconciliationRow> = [
  {
    title: 'Organization / Sensor',
    key: 'name',
    width: 320,
    render: (_, record) =>
      record.kind === 'organization' ? (
        <Flex gap={8} align="center" wrap>
          <Typography.Text strong>{record.name}</Typography.Text>
          <Tag>
            {record.sensorCount}{' '}
            {record.sensorCount === 1 ? 'sensor' : 'sensors'}
          </Tag>
        </Flex>
      ) : (
        <Flex vertical gap={2}>
          <Typography.Text code style={{ whiteSpace: 'nowrap' }}>
            {record.name}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {record.building}
          </Typography.Text>
          {record.tags.length > 0 && (
            <Flex gap={4} wrap>
              {record.tags.map((tag) => (
                <Tooltip key={tag.label} title={tag.tooltip}>
                  <Tag color={tag.color} style={{ marginInlineEnd: 0 }}>
                    {tag.label}
                  </Tag>
                </Tooltip>
              ))}
            </Flex>
          )}
        </Flex>
      ),
  },
  {
    title: 'Vantage',
    children: [
      countColumn(
        'Assets',
        'assets',
        'Assets Vantage returned for the sensor in this run: assets with activity since the previous sync, not the full inventory.',
      ),
    ],
  },
  {
    title: 'Import Outcome',
    children: [
      countColumn(
        'Matched',
        'matched',
        'Matched an existing portal device, which was updated.',
      ),
      countColumn('Created', 'created', 'A new portal device was created.'),
      countColumn(
        'Not Under Contract',
        'notUnderContract',
        'Matched an existing portal device, which was not updated because the building has no active Threat Detection or Asset Inventory line.',
      ),
    ],
  },
  {
    title: 'Skipped by Reason',
    children: [
      countColumn(
        'No Unique MAC',
        'noUniqueMac',
        'Every MAC on the asset is locally administered or randomized, so there is nothing safe to match on.',
      ),
      countColumn(
        'No Contract',
        'notUnderContractNew',
        'A new asset that was not created because the building is not under contract.',
      ),
      countColumn(
        'Multiple Matches',
        'multipleMatches',
        'The asset matches more than one portal device. Merge the duplicates.',
      ),
      countColumn(
        'No Building',
        'noBuilding',
        'Neither the sensor nor the asset’s zone mapping assigns a building.',
      ),
      countColumn(
        'Other',
        'otherSkipped',
        'Device creation failed or is disabled for the sync.',
      ),
    ],
  },
  {
    title: 'Portal Devices',
    children: [
      countColumn(
        'Synced',
        'portalSynced',
        'Active portal devices with a Nozomi key from this sensor refreshed in this run.',
      ),
      countColumn(
        'Active',
        'portalActive',
        'All active portal devices linked to this sensor.',
      ),
      countColumn(
        'Not Seen',
        'notSeen',
        'Active devices whose Nozomi key was not refreshed this run. Devices unseen for 14 days are retired automatically.',
      ),
    ],
  },
  {
    title: 'Reconciliation',
    children: [
      {
        title: (
          <Tooltip title="Vantage assets per portal device. Nozomi often creates one asset per network interface, and assets behind a Tempered Airwall share its MAC, so several assets can land on one device.">
            Assets per Device
          </Tooltip>
        ),
        key: 'assetsPerDevice',
        align: 'right',
        width: 150,
        render: (_, record) => <AssetsPerDevice counts={record} />,
      },
      {
        title: (
          <Tooltip title="Assets − skipped − assets on portal devices. 0 means the row reconciles.">
            Variance
          </Tooltip>
        ),
        key: 'variance',
        align: 'right',
        width: 90,
        render: (_, record) => <Variance counts={record} />,
      },
    ],
  },
];

// Leaf columns after the name column, in display order, for the totals row.
const totalCells: ((totals: ReconciliationCounts) => React.ReactNode)[] = [
  (totals) => formatCount(totals.assets),
  (totals) => formatCount(totals.matched),
  (totals) => formatCount(totals.created),
  (totals) => formatCount(totals.notUnderContract),
  (totals) => formatCount(totals.noUniqueMac),
  (totals) => formatCount(totals.notUnderContractNew),
  (totals) => formatCount(totals.multipleMatches),
  (totals) => formatCount(totals.noBuilding),
  (totals) => formatCount(totals.otherSkipped),
  (totals) => formatCount(totals.portalSynced),
  (totals) => formatCount(totals.portalActive),
  (totals) => formatCount(totals.notSeen),
  (totals) => <AssetsPerDevice counts={totals} />,
  (totals) => <Variance counts={totals} />,
];

const NozomiReconciliationTable = () => {
  const { data, loading, filter, expandedKeys, onExpandedKeysChange } =
    useContext(NozomiReconciliationContext);
  const [pageSize, setPageSize] = useState<number>(25);

  const sites = useMemo(() => data?.sites ?? [], [data]);
  const rows = useMemo(() => buildRows(sites, filter), [sites, filter]);
  const totals = useMemo(() => getTotals(sites), [sites]);

  return (
    <IBTable<ReconciliationRow>
      columns={columns}
      dataSource={rows}
      loading={loading}
      rowKey="key"
      expandable={{
        expandedRowKeys: expandedKeys,
        onExpandedRowsChange: (keys) => onExpandedKeysChange(keys.map(String)),
      }}
      pagination={{
        pageSize,
        showSizeChanger: true,
        onShowSizeChange: (_, size) => setPageSize(size),
      }}
      summary={() =>
        sites.length > 0 && (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0}>
              <Typography.Text strong>All organizations</Typography.Text>
            </Table.Summary.Cell>
            {totalCells.map((render, idx) => (
              <Table.Summary.Cell key={idx} index={idx + 1} align="right">
                <Typography.Text strong>{render(totals)}</Typography.Text>
              </Table.Summary.Cell>
            ))}
          </Table.Summary.Row>
        )
      }
    />
  );
};

export default NozomiReconciliationTable;
